// src/index.ts
import { z } from "zod";
var FetcherClient = class {
  ctxSchema;
  headersShape;
  constructor(options) {
    this.ctxSchema = options.ctx;
    if ("headers" in options) {
      this.headersShape = options.headers;
    }
  }
  fetcher(fetcherDefinition) {
    return fetcherDefinition;
  }
  combineFetchers(fetcherDefs) {
    return (options) => {
      const fetchers = Object.entries(fetcherDefs).reduce(
        (acc, [key, fetcher]) => {
          acc[key] = async (input) => {
            const opts = typeof options === "function" ? options() : options;
            const ctx = this.ctxSchema.parse(opts.ctx);
            const get = this.createGet(
              () => this.getHeadersFromOptions(options)
            );
            const post = this.createPost(
              () => this.getHeadersFromOptions(options)
            );
            const patch = this.createPatch(
              () => this.getHeadersFromOptions(options)
            );
            const put = this.createPut(
              () => this.getHeadersFromOptions(options)
            );
            const del = this.createDelete(
              () => this.getHeadersFromOptions(options)
            );
            const fetcherOpts = {
              ctx,
              headers: this.getHeadersFromOptions(options),
              get,
              post,
              patch,
              put,
              delete: del
            };
            try {
              const result = await fetcher(fetcherOpts, input);
              return result;
            } catch (error) {
              if (opts.postProcess?.onError) {
                return opts.postProcess.onError({
                  options: fetcherOpts,
                  input,
                  error,
                  fetcher,
                  fetcherName: key
                });
              }
              throw error;
            }
          };
          return acc;
        },
        {}
      );
      return fetchers;
    };
  }
  getHeadersFromOptions = (options) => {
    const opts = typeof options === "function" ? options() : options;
    const headers = this.headersShape && "headers" in opts ? z.object(this.headersShape).parse(opts.headers) : void 0;
    return headers;
  };
  createGet = (getHeaders) => {
    const get = async (url, schema, options) => {
      const response = await fetch(url, {
        referrerPolicy: "no-referrer",
        ...options,
        headers: {
          ...getHeaders() ?? {},
          ...options?.headers
        }
      });
      return { data: schema.parse(await response.json()), response };
    };
    return get;
  };
  createPost = (getHeaders) => {
    const post = async (url, schema, data, options) => {
      const response = await fetch(url, {
        method: "POST",
        cache: "no-cache",
        referrerPolicy: "no-referrer",
        body: JSON.stringify(data),
        ...options,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          ...getHeaders() ?? {},
          ...options?.headers
        }
      });
      return { data: schema.parse(await response.json()), response };
    };
    return post;
  };
  createPatch = (getHeaders) => {
    const patch = async (url, schema, data, options) => {
      const response = await fetch(url, {
        method: "PATCH",
        cache: "no-cache",
        referrerPolicy: "no-referrer",
        body: JSON.stringify(data),
        ...options,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          ...getHeaders() ?? {},
          ...options?.headers
        }
      });
      return { data: schema.parse(await response.json()), response };
    };
    return patch;
  };
  createPut = (getHeaders) => {
    const put = async (url, schema, data, options) => {
      const response = await fetch(url, {
        method: "PUT",
        cache: "no-cache",
        referrerPolicy: "no-referrer",
        body: JSON.stringify(data),
        ...options,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          ...getHeaders() ?? {},
          ...options?.headers
        }
      });
      return { data: schema.parse(await response.json()), response };
    };
    return put;
  };
  createDelete = (getHeaders) => {
    const del = async (url, schema, data, options) => {
      const response = await fetch(url, {
        method: "DELETE",
        cache: "no-cache",
        referrerPolicy: "no-referrer",
        body: JSON.stringify(data),
        ...options,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          ...getHeaders() ?? {},
          ...options?.headers
        }
      });
      return { data: schema.parse(await response.json()), response };
    };
    return del;
  };
};
export {
  FetcherClient
};
